/**
 * ManualTiposSolucion.jsx
 * v99.5.3 · Renderizador del manual de Tipos de Solución.
 * Secciones navegables, índice lateral, buscador, SVG mermaid, PDF.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Search, Download, ChevronRight, BookOpen, List, Settings,
  TreePine, SlidersHorizontal, Database, Footprints, Wrench,
  ShieldAlert, HelpCircle, GitBranch,
} from "lucide-react";

/* ── Secciones del manual (parseadas del .md) ── */
import manualRaw from "../docs/manual-tipos-solucion.md";

const SECTION_ICONS = [
  BookOpen, GitBranch, Settings, TreePine, SlidersHorizontal,
  Database, Footprints, Wrench, ShieldAlert, HelpCircle,
];

/* ── helpers ── */
export function parseSections(raw) {   // v101.129.0 · también lo usa el Centro de Validaciones
  const lines = raw.split("\n");
  const sections = [];
  let current = null;
  for (const line of lines) {
    const m = line.match(/^## (\d+)\.\s+(.+)$/);
    if (m) {
      if (current) sections.push(current);
      current = { num: parseInt(m[1]), title: m[2], lines: [] };
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

function sectionId(num) { return `seccion-${num}`; }

/* ── Mermaid → SVG (hard-coded, same content as the .md block) ── */
function FlowDiagramSVG() {
  return (
    <div className="my-6 overflow-x-auto" data-testid="mermaid-diagram">
      <svg viewBox="0 0 960 340" className="w-full max-w-[960px] mx-auto" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
        <defs>
          <marker id="arrowEnd" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#64748b" />
          </marker>
        </defs>
        {/* Nodes */}
        <rect x="10" y="60" width="170" height="52" rx="8" fill="#EFF6FF" stroke="#93C5FD" strokeWidth="1.5" />
        <text x="95" y="82" textAnchor="middle" fontSize="11" fontWeight="600" fill="#1E40AF">Tarea de Odoo</text>
        <text x="95" y="97" textAnchor="middle" fontSize="10" fill="#3B82F6">código de actividad</text>

        <rect x="220" y="60" width="160" height="52" rx="8" fill="#ECFDF5" stroke="#6EE7B7" strokeWidth="1.5" />
        <text x="300" y="82" textAnchor="middle" fontSize="11" fontWeight="600" fill="#065F46">Tipos hoja</text>
        <text x="300" y="97" textAnchor="middle" fontSize="10" fill="#059669">con ese código Odoo</text>

        {/* Diamond */}
        <polygon points="490,40 560,86 490,132 420,86" fill="#FFF7ED" stroke="#FDBA74" strokeWidth="1.5" />
        <text x="490" y="80" textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#9A3412">¿Uno solo con</text>
        <text x="490" y="93" textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#9A3412">segmento?</text>

        {/* Default path */}
        <rect x="430" y="175" width="120" height="48" rx="8" fill="#FEF9C3" stroke="#FDE047" strokeWidth="1.5" />
        <text x="490" y="195" textAnchor="middle" fontSize="10" fontWeight="600" fill="#854D0E">Predeterminado</text>
        <text x="490" y="208" textAnchor="middle" fontSize="10" fill="#A16207">★ DEFAULT</text>

        {/* Result */}
        <rect x="620" y="60" width="170" height="52" rx="8" fill="#0A3747" stroke="#0A3747" strokeWidth="1.5" />
        <text x="705" y="82" textAnchor="middle" fontSize="11" fontWeight="700" fill="#E0E621">Tipo de solución</text>
        <text x="705" y="97" textAnchor="middle" fontSize="10" fill="#ffffff">vinculado a la OT</text>

        {/* Outputs */}
        <rect x="830" y="20" width="120" height="36" rx="6" fill="#EDE9FE" stroke="#C4B5FD" strokeWidth="1" />
        <text x="890" y="43" textAnchor="middle" fontSize="10" fontWeight="600" fill="#5B21B6">Receta</text>

        <rect x="830" y="66" width="120" height="36" rx="6" fill="#FCE7F3" stroke="#F9A8D4" strokeWidth="1" />
        <text x="890" y="89" textAnchor="middle" fontSize="10" fontWeight="600" fill="#9D174D">Formulario</text>

        <rect x="830" y="112" width="120" height="36" rx="6" fill="#FEF3C7" stroke="#FCD34D" strokeWidth="1" />
        <text x="890" y="135" textAnchor="middle" fontSize="10" fontWeight="600" fill="#92400E">SLA</text>

        <rect x="830" y="158" width="120" height="36" rx="6" fill="#D1FAE5" stroke="#6EE7B7" strokeWidth="1" />
        <text x="890" y="181" textAnchor="middle" fontSize="10" fontWeight="600" fill="#065F46">Aprovisionamiento</text>

        {/* Arrows */}
        <line x1="180" y1="86" x2="220" y2="86" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#arrowEnd)" />
        <line x1="380" y1="86" x2="420" y2="86" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#arrowEnd)" />
        <line x1="560" y1="86" x2="620" y2="86" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#arrowEnd)" />
        <text x="590" y="78" fontSize="9" fill="#059669" fontWeight="600">sí</text>

        <line x1="490" y1="132" x2="490" y2="175" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#arrowEnd)" />
        <text x="500" y="157" fontSize="9" fill="#DC2626" fontWeight="600">no</text>

        <line x1="550" y1="199" x2="625" y2="112" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#arrowEnd)" />

        <line x1="790" y1="78" x2="830" y2="38" stroke="#64748b" strokeWidth="1" markerEnd="url(#arrowEnd)" />
        <line x1="790" y1="84" x2="830" y2="84" stroke="#64748b" strokeWidth="1" markerEnd="url(#arrowEnd)" />
        <line x1="790" y1="90" x2="830" y2="130" stroke="#64748b" strokeWidth="1" markerEnd="url(#arrowEnd)" />
        <line x1="790" y1="96" x2="830" y2="176" stroke="#64748b" strokeWidth="1" markerEnd="url(#arrowEnd)" />
      </svg>
      <p className="text-center text-[10px] text-gray-400 mt-1">Flujo de asignación automática del tipo de solución</p>
    </div>
  );
}

/* ── Inline markdown renderer ── */
function renderInline(text) {
  if (!text) return text;
  // Bold **text** and __text__
  let out = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__(.+?)__/g, '<strong>$1</strong>');
  // Code `text`
  out = out.replace(/`(.+?)`/g, '<code class="text-[11px] bg-gray-100 px-1 py-0.5 rounded font-mono text-[#0A3747]">$1</code>');
  // «text»
  out = out.replace(/«(.+?)»/g, '<em class="font-medium text-[#0A3747]">«$1»</em>');
  return out;
}

function Prose({ html }) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ── Table renderer ── */
function MdTable({ lines }) {
  const headerLine = lines[0];
  const dataLines = lines.slice(2); // skip separator
  const headers = headerLine.split("|").map((h) => h.trim()).filter(Boolean);
  const rows = dataLines.map((l) => l.split("|").map((c) => c.trim()).filter(Boolean));

  return (
    <div className="overflow-x-auto my-4 border border-gray-200 rounded-lg">
      <table className="w-full text-left text-[12px]">
        <thead>
          <tr className="bg-[#0A3747]/5 border-b border-gray-200">
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 font-bold text-[#0A3747] text-[11px] uppercase tracking-wider whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.filter((r) => r.length > 0).map((row, i) => (
            <tr key={i} className="hover:bg-gray-50/50">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-gray-700 text-[12px] leading-relaxed align-top">
                  <Prose html={renderInline(cell)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Section renderer ── */
export function SectionContent({ section, searchTerm }) {   // v101.129.0
  const blocks = useMemo(() => {
    const result = [];
    let i = 0;
    const lines = section.lines;
    while (i < lines.length) {
      const line = lines[i];
      // Skip mermaid code block
      if (line.trim().startsWith("```mermaid")) {
        result.push({ type: "mermaid" });
        i++; // skip ```mermaid line
        while (i < lines.length && !lines[i].trim().startsWith("```")) i++;
        i++; // skip closing ```
        continue;
      }
      // Table detection
      if (i + 1 < lines.length && lines[i + 1]?.match(/^\|[\s-|]+\|$/)) {
        const tableLines = [];
        while (i < lines.length && lines[i].startsWith("|")) {
          tableLines.push(lines[i]);
          i++;
        }
        result.push({ type: "table", lines: tableLines });
        continue;
      }
      // Bold subheading **text**
      if (line.match(/^\*\*.+\*\*$/)) {
        result.push({ type: "subheading", text: line.replace(/\*\*/g, "") });
        i++;
        continue;
      }
      // Ordered list
      if (line.match(/^\d+\.\s/)) {
        const items = [];
        while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
          items.push(lines[i].replace(/^\d+\.\s+/, ""));
          i++;
        }
        result.push({ type: "ol", items });
        continue;
      }
      // Unordered list
      if (line.match(/^- /)) {
        const items = [];
        while (i < lines.length && lines[i].match(/^- /)) {
          items.push(lines[i].replace(/^- /, ""));
          i++;
        }
        result.push({ type: "ul", items });
        continue;
      }
      // Paragraph
      if (line.trim()) {
        result.push({ type: "p", text: line });
      }
      i++;
    }
    return result;
  }, [section]);

  const highlight = (text) => {
    if (!searchTerm || !text) return renderInline(text);
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const html = renderInline(text);
    return html.replace(new RegExp(`(${escaped})`, "gi"), '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>');
  };

  return (
    <div className="space-y-3">
      {blocks.map((b, idx) => {
        switch (b.type) {
          case "mermaid": return <FlowDiagramSVG key={idx} />;
          case "table": return <MdTable key={idx} lines={b.lines} />;
          case "subheading": return (
            <h4 key={idx} className="text-[13px] font-bold text-[#0A3747] mt-4 mb-1 border-b border-gray-100 pb-1">
              <Prose html={highlight(b.text)} />
            </h4>
          );
          case "ol": return (
            <ol key={idx} className="space-y-2 pl-1 my-3">
              {b.items.map((item, j) => (
                <li key={j} className="flex gap-3 text-[13px] text-gray-700 leading-relaxed">
                  <span className="w-5 h-5 rounded-full bg-[#0A3747] text-white flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">{j + 1}</span>
                  <span className="flex-1"><Prose html={highlight(item)} /></span>
                </li>
              ))}
            </ol>
          );
          case "ul": return (
            <ul key={idx} className="space-y-1.5 pl-4 my-2">
              {b.items.map((item, j) => (
                <li key={j} className="text-[13px] text-gray-700 leading-relaxed flex gap-2">
                  <span className="text-[#E0E621] mt-1 flex-shrink-0">&#9679;</span>
                  <span className="flex-1"><Prose html={highlight(item)} /></span>
                </li>
              ))}
            </ul>
          );
          case "p": return (
            <p key={idx} className="text-[13px] text-gray-700 leading-relaxed text-justify">
              <Prose html={highlight(b.text)} />
            </p>
          );
          default: return null;
        }
      })}
    </div>
  );
}

/* ── Main component ── */
export function ManualTiposSolucionContent() {
  const location = useLocation();
  const sections = useMemo(() => parseSections(manualRaw), []);
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState(1);
  const sectionRefs = useRef({});

  // Jump to hash section on mount
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const m = hash.match(/#(\d+)/);
      if (m) {
        const num = parseInt(m[1]);
        setActiveSection(num);
        setTimeout(() => {
          const el = document.getElementById(sectionId(num));
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    }
  }, [location.hash]);

  const scrollTo = (num) => {
    setActiveSection(num);
    const el = document.getElementById(sectionId(num));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const lower = search.toLowerCase();
    return sections.filter((s) =>
      s.title.toLowerCase().includes(lower) || s.lines.some((l) => l.toLowerCase().includes(lower))
    );
  }, [sections, search]);

  const handlePrint = () => { window.print(); };

  return (
    <div className="space-y-6" data-testid="manual-tipos-solucion">
      {/* Hero */}
      <div
        className="rounded-2xl p-6 md:p-8 border border-[#E0E621]/30 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0A3747 0%, #135166 100%)" }}
      >
        <div className="absolute right-6 top-6 opacity-10">
          <BookOpen size={160} className="text-[#E0E621]" />
        </div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E0E621] mb-2">
            Manual operativo · v4.3.0
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "Chivo" }}>
            Tipos de Solución
          </h2>
          <p className="text-white/80 max-w-2xl text-sm">
            Referencia completa del módulo: cómo se enlazan con Odoo, el árbol, el panel de reglas, el catálogo de aprovisionamiento y el uso desde la OT.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E0E621] text-[#0A3747] text-xs font-bold hover:bg-[#d4da1a] transition"
              data-testid="btn-download-pdf"
            >
              <Download size={14} /> Descargar PDF
            </button>
            <span className="text-[10px] text-white/50">10 secciones · {sections.reduce((a, s) => a + s.lines.length, 0)} líneas</span>
          </div>
        </div>
      </div>

      {/* Layout: sidebar + content */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0 print:hidden">
          <div className="sticky top-20">
            {/* Search */}
            <div className="relative mb-4">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar en el manual…"
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:border-[#0A3747] focus:ring-1 focus:ring-[#0A3747]/20 outline-none"
                data-testid="manual-search"
              />
            </div>
            {/* Index */}
            <nav className="space-y-0.5" data-testid="manual-sidebar">
              {sections.map((s) => {
                const Icon = SECTION_ICONS[s.num - 1] || List;
                const isActive = activeSection === s.num;
                const isInFilter = filteredSections.some((f) => f.num === s.num);
                return (
                  <button
                    key={s.num}
                    onClick={() => scrollTo(s.num)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] transition ${
                      isActive ? "bg-[#0A3747] text-white font-semibold" : isInFilter ? "text-gray-700 hover:bg-gray-100" : "text-gray-300"
                    }`}
                    data-testid={`sidebar-section-${s.num}`}
                  >
                    <Icon size={13} className={isActive ? "text-[#E0E621]" : ""} />
                    <span className="truncate">{s.num}. {s.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 space-y-8">
          {/* Mobile search */}
          <div className="lg:hidden relative mb-4 print:hidden">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar en el manual…"
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg outline-none"
            />
          </div>

          {filteredSections.map((s) => {
            const Icon = SECTION_ICONS[s.num - 1] || List;
            return (
              <section
                key={s.num}
                id={sectionId(s.num)}
                ref={(el) => { sectionRefs.current[s.num] = el; }}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
                data-testid={`section-${s.num}`}
              >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                  <div className="w-8 h-8 rounded-lg bg-[#0A3747] flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-[#E0E621]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-[#0A3747]">{s.num}. {s.title}</h3>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                </div>
                <div className="px-5 py-4">
                  <SectionContent section={s} searchTerm={search} />
                </div>
              </section>
            );
          })}

          {filteredSections.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">
              No se encontraron resultados para "{search}"
            </div>
          )}
        </main>
      </div>

      <div className="text-center text-[10px] text-gray-400 py-4 print:hidden">
        Manual de Tipos de Solución · Alfa-Field Connect v4.3.0
      </div>
    </div>
  );
}
